import type { LocationContext } from "@/lib/location-ranking";

/** A specific neighbourhood / area within a city (e.g. "Connaught Place"). */
export interface LocalityNode {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

/** A city, optionally broken down into browsable localities. */
export interface CityNode {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  localities: LocalityNode[];
}

/**
 * A metro region that groups several cities (e.g. "Delhi/NCR" → Delhi,
 * Gurgaon, Noida …). Powers the second-level "region" drawer step.
 */
export interface RegionNode {
  id: string;
  name: string;
  /** Centroid — used for the "Anywhere in <region>" option. */
  latitude: number;
  longitude: number;
  /** Radius that "Anywhere in <region>" searches across. */
  radiusKm: number;
  cities: CityNode[];
}

export type CityIllustrationKey =
  | "delhi"
  | "mumbai"
  | "kolkata"
  | "bengaluru"
  | "hyderabad"
  | "chandigarh"
  | "generic";

/** A featured city shown in the "Popular cities" rail with an illustration. */
export interface PopularCity {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  illustration: CityIllustrationKey;
  /**
   * When set, selecting this card opens the region detail step rather than
   * immediately selecting a single point.
   */
  regionId?: string;
  /** When set, selecting this card opens the single-city locality step. */
  cityKey?: string;
}

/** A flat A–Z entry in the "All cities" list. */
export interface AllCityEntry {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  /** When set, picking this city opens its region detail step. */
  regionId?: string;
  /** When set, picking this city opens the single-city locality step. */
  cityKey?: string;
}

/** A single city broken into browsable localities (the city-detail step). */
export interface CityDetail {
  key: string;
  name: string;
  latitude: number;
  longitude: number;
  localities: LocalityNode[];
}

/** A normalized hit produced when searching the curated dataset. */
export interface CuratedSearchResult {
  id: string;
  /** Primary label, e.g. "Saket". */
  name: string;
  /** Secondary context, e.g. "Delhi · Delhi/NCR". */
  context?: string;
  latitude: number;
  longitude: number;
  /** When set, selecting the hit opens the region detail step. */
  regionId?: string;
  /** When set, selecting the hit opens the single-city locality step. */
  cityKey?: string;
  radiusKm?: number;
}

export type LocationSource = "profile" | "custom" | "current" | "ip";

/** The resolved location the whole app derives location-aware data from. */
export interface SelectedLocation {
  latitude: number;
  longitude: number;
  displayName: string;
  /** Optional override radius (e.g. region-wide "Anywhere in …"). */
  radiusKm?: number;
  source: LocationSource;
}

export function selectedLocationToContext(
  location: SelectedLocation | null,
): LocationContext | null {
  if (!location) return null;
  return { latitude: location.latitude, longitude: location.longitude };
}

/** Stable cache key for a selected location (matches the hiives convention). */
export function locationKeyFor(location: SelectedLocation | null): string {
  if (!location) return "none";
  return `${location.latitude}:${location.longitude}`;
}
